import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { NgForm } from '@angular/forms';
import { Observable, throwError } from 'rxjs';
import { IUser } from '../models/user.model';
import { apiParameters } from 'src/app/configs/api.params';
import { IClientModel, IClients } from '../models/application.model';
import { ErrorService } from './error.service';
import { IError } from '../models/error.model';



@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  constructor(
    private httpClient: HttpClient,
    private errorService: ErrorService
  ) { }
  
  /**
   * Gets all customers
   * @returns all customers
   */
  getAll(): Observable<IUser[]> {
    return this.httpClient.get(apiParameters.urlV2 + '/customers')
      .pipe(
        map((res: IUser[]) => {
          return res;
        })
      );
  }

  /**
   * Gets one customer
   * @param id - customer id
   * @returns Customer 
   */
  get(id: string): Observable<any> {
    return this.httpClient.get<any>(apiParameters.urlV2 + '/customers/' + id)
      .pipe(
        map((res) => {
          if (res.customers) {
            res.customers = res.customers.sort((a, b) => {
              return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
            });
          }
          return res;
        })
      );
  }

  /**
   * This method searchs client as customer
   * @param customerId - customer id
   * @param searchFields - array of object field e.g. [{"fieldNumber": 0,"fieldKey": "name","fieldValue": "test","button": "add"}]
   * @returns List of filtered clients
   */
  getApplications(customerId: string, searchFields?: any): Observable<IClientModel[]> {
    let searchParams = new HttpParams();

    if (searchFields && searchFields.length) {
      searchFields.forEach(field => {
        if (field.fieldKey !== '' && field.fieldValue !== '') {
          searchParams = searchParams.append(field.fieldKey, field.fieldValue);
        }
      });
    }

    const httpOptions = {
      params: searchParams
    };

    return this.httpClient.get<IClientModel[]>(`${apiParameters.urlV2}/customers/${customerId}/clients`, httpOptions)
      .pipe(
        map((res: IClientModel[]) => {
          res.sort((a,b) => {
            return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
          });
          return res;
        })
      );
  }

  /**
   * Add new customer
   * @param form - form data(name, phone, allowChilds, allowNegativeCredits)
   * @param parentId - parent id if the customer(subcustomer) has a parent customer
   * @returns Added customer
   */
  add(form: NgForm, parentId?: string): Observable<any> {

    const name = form.form.value.name;
    const phone = form.form.value.phone === '' ? null : form.form.value.phone;
    const infoEmail = form.form.value.infoEmail === '' ? null : form.form.value.infoEmail;
    const allowChilds = form.form.value.allowChilds === true ? true : false;
    let allowNegativeCredits;
    let geoLocationEnabled;
    
    if (form.form.value.allowNegativeCredits)
      allowNegativeCredits =
        form.form.value.allowNegativeCredits === true ? true : false;

    if (form.form.value.geoLocationEnabled)
      geoLocationEnabled =
          form.form.value.geoLocationEnabled === true ? true : false;  

    const body = {
      name,
      phone,
      allowChilds,
      allowNegativeCredits,
      geoLocationEnabled,
      infoEmail
    };

    if (parentId) {
      body['parentId'] = parentId;
    }
    console.log(body)
    return this.httpClient.post(apiParameters.urlV2 + '/customers', body)
      .pipe(map((res) => {
        return res;
      }));
  }

  /**
   * Adds new customer as root
   * @param body 
   * @param parentId - parent id if the customer(subcustomer) has a parent customer 
   * @returns customer as root 
   */
  addCustomerAsRoot(body: any, parentId?: string): Observable<any> {

    if (parentId) {
      body['parentId'] = parentId;
    }
    return this.httpClient.post(apiParameters.urlV2 + '/customers', body)
      .pipe(map((res) => {
        return res;
      }));
  }

  /**
   * Adds admin
   * @param form - admin data entered in NgForm
   * @param customerId 
   * @returns admin 
   */
  addAdmin(form: NgForm, customerId: string): Observable<IUser> {

    const email = form.form.value.email;
    const name = form.form.value.name;
    const password = form.form.value.password;
    const phone = form.form.value.phone === '' ? null : form.form.value.phone;
    const readOnlyPermissions = form.form.value.readOnlyPermissions ? true : false;

    const body = {
      email,
      name,
      password,
      phone,
      readOnlyPermissions
    };
    const url = `${apiParameters.urlV2}/customers/${customerId}/admins`;
    return this.httpClient.post(url, body)
      .pipe(map((res: IUser) => {
        return res;
      }));
  }

  /**
   * Edit customer
   * @param body - {name, phone, allowChilds, allowNegativeCredits};
   * @param customerId 
   * @returns Changed customer
   */
  patch(body, customerId: string): Observable<any> {
    // return this.authService.LoggedinUser
    //   .pipe(
    //     take(1),
    //     concatMap(customer => {
    return this.httpClient.patch(`${apiParameters.urlV2}/customers/${customerId}`, body);
    //   })
    // );
  }

  deleteCustomer(customerId: string): Observable<any> {
    return this.httpClient.delete(apiParameters.urlV2 + '/customers/' + customerId);
  }

  addCredit(customerId: string, credit: number, creditType: string = 'credits'): Observable<any> {
    return this.httpClient.post(apiParameters.url + '/customers/' + customerId + '/credit', { total: credit, creditType: creditType });
  }

  transferCredits(customerId: string, credit: number, fromCustomerId: string, creditType: string = 'credits'): Observable<any> {
    return this.httpClient.post(apiParameters.url + '/customers/' + customerId + '/transfer-credits', { fromCustomerId, amount: credit, creditType: creditType });
  }

  private handleError(errorRes: HttpErrorResponse) {
    let error = {} as IError;
    error = this.errorService.whichError(errorRes);
    return throwError(error);
  }

  getById(id: string): Observable<IUser> {
    const httpOptions = {
      headers: new HttpHeaders({
      })
    };
    return this.httpClient.get(apiParameters.urlV2 + '/customers/' + id, httpOptions)
      .pipe(map((res: IUser) => {
        return res;
      })
      );
  }

  getCustomerAdmins(customerId: string, page, limit): Observable<any> {

    let httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      }),
    }

    const url = `${apiParameters.urlV2}/customers/${customerId}/admins`;
    return this.httpClient.get(url, httpOptions)
      .pipe(map((res) => {
        return res;
      }));
  }

  getCustomerAdmin(customerId: string, adminId: string): Observable<IUser> {
    const url = `${apiParameters.urlV2}/customers/${customerId}/admins/${adminId}`;
    return this.httpClient.get(url)
      .pipe(map((res: IUser) => {
        return res;
      }));
  }

  updateCustomerAdmin(customerId: string, adminId: string, body): Observable<any> {
    const url = `${apiParameters.urlV2}/customers/${customerId}/admins/${adminId}`;
    return this.httpClient.patch(url, body)
      .pipe(map((res) => {
        return res;
      }));
  }

  deleteCustomerAdmin(customerId: string, adminId: string): Observable<IUser> {
    const url = `${apiParameters.urlV2}/customers/${customerId}/admins/${adminId}`;
    return this.httpClient.delete(url)
      .pipe(map((res: IUser) => {
        return res;
      }));
  }

  getSubcustomerUsers(customerId: string, clientId: string): Observable<IUser[]> {
    const url = `${apiParameters.urlV2}/customers/${customerId}/clients/${clientId}/users`;
    return this.httpClient.get(url)
      .pipe(map((users: IUser[]) => users.sort((a, b) => {
        return (new Date(b.lastTimeOnline) as any) - (new Date(a.lastTimeOnline) as any);
      })));
  }

  addUser(body): Observable<IUser> {
    return this.httpClient.post(apiParameters.url + '/users', body)
      .pipe(map((res: IUser) => {
        return res;
      })
      );
  }

  getCustomerSettings(customerId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/settings`);
  }

  updateCustomerSettings(customerId: string, body): Observable<any> {
    return this.httpClient.patch(`${apiParameters.url}/customers/${customerId}/settings`, body);
  }

  createInvoice(customerId: string): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/customers/${customerId}/invoices`, {});
  }

  getInvoices(customerId: string, page: number, limit: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      })
    };

    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/invoices`, httpOptions)
      .pipe(
        map((res: any) => {
          res.results.sort((a, b) => {
            return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
          });
          return res;
        })
      );
  }

  getInvoiceById(customerId: string, invoiceId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/invoices/${invoiceId}`);
  }

  closeInvoice(customerId: string, invoiceId: string): Observable<any> {
    return this.httpClient.patch(`${apiParameters.url}/customers/${customerId}/invoices/${invoiceId}/close`, {});
  }

  generateCSV(customerId: string, invoiceId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/invoices/${invoiceId}/csv`);
  }

  getInvoiceItems(customerId: string, invoiceId: string, page: number, limit: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      })
    };

    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/invoices/${invoiceId}/items`, httpOptions);
  }

  getAllApplications(customerId: string, page?: number, limit?: number, searchFields?: any): Observable<IClients> {
    let httpOptions = {};

    page && limit
      ? httpOptions = {
        headers: new HttpHeaders({
          page: page.toString(),
          limit: limit.toString(),
        }),
        params: searchFields,
      }
      : httpOptions = {
        params: searchFields,
      };

    return this.httpClient.get(`${apiParameters.urlV2}/customers/${customerId}/subcustomer/clients`, httpOptions)
      .pipe(
        map((res: IClients) => {
          return res;
        })
      );
  }

  generateClientsCSV(customerId: string, fromDate:string, toDate: string, customInvoiceConfigEnable?: any, customInvoiceConfigComboEnable?: any): Observable<any> {
    let searchParams = new HttpParams();

    searchParams = searchParams.append('startDate', fromDate);
    searchParams = searchParams.append('endDate', toDate);

    if (customInvoiceConfigEnable) {
      searchParams = searchParams.append('customInvoiceConfigEnable', customInvoiceConfigEnable);
    }
    if (customInvoiceConfigComboEnable) {
      searchParams = searchParams.append('customInvoiceConfigComboEnable', customInvoiceConfigComboEnable);
    }
    
    const httpOptions = {
      params: searchParams
    };

    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/invoices/csv`, httpOptions);
  }

  getClientsInvoices(customerId: string, page: number, limit: number, fromDate:string, toDate: string ): Observable<any> {
    let searchParams = new HttpParams();

    searchParams = searchParams.append('startDate', fromDate);
    searchParams = searchParams.append('endDate', toDate);

    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      }),
      params: searchParams
    };

    return this.httpClient.get(`${apiParameters.url}/customers/${customerId}/clients/invoices`, httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        })
      );
  }

  transferCustomer(customerId: string, body): Observable<any> {
    return this.httpClient.patch(`${apiParameters.urlV2}/customers/${customerId}/transfer`, body)
      .pipe(map((res) => {
        return res;
      }));;
  }
}
